<template>
  <!--
    The promo card's countdown (FreePtPromo), from the same countdown() as the landing page.
    It keeps its own clock, so only this re-renders each second, not the card. In the last
    second of the run it renders nothing and emits `ended`, so it never shows all zeros.
  -->
  <div v-if="live && !ended" class="free-pt-countdown">
    <span class="label">{{ clock.label }}</span>
    <span class="digits" role="timer" aria-live="off">{{ clock.compact }}</span>
  </div>
  <!-- The saved (prerendered) page shows the date: digits saved at build time would be wrong. -->
  <div v-else-if="!live" class="free-pt-countdown">
    <span class="label">{{ clock.upcoming ? 'Sessions start' : 'Sessions end' }}</span>
    <span class="date">{{ targetDate }}</span>
  </div>
</template>

<script>
import { countdown, ukDate } from '@/data/freePtCampaign.js';

export default {
  name: 'FreePtCountdown',
  props: {
    run: { type: Object, required: true },
  },
  emits: ['ended'],
  data() {
    return {
      now: Date.now(),
      // False in the prerender. No hydration (main.js createApp), so a real browser's first render already has the digits.
      live: typeof window !== 'undefined' && !window.__PRERENDER__,
    };
  },
  computed: {
    clock() { return countdown(this.run, this.now); },
    ended() { return !this.clock.upcoming && this.clock.left === 0; },
    // "Sat 28 November", or "Mon 26 October" before the run starts.
    targetDate() { return ukDate(this.clock.target, { weekday: 'short', day: 'numeric', month: 'long' }); },
  },
  watch: {
    ended: {
      immediate: true,
      handler(ended) { if (ended && this.live) this.$emit('ended'); },
    },
  },
  mounted() {
    if (!this.live) return;
    this._tick = setInterval(() => { this.now = Date.now(); }, 1000);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  },
  unmounted() {
    clearInterval(this._tick);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    // A phone back from the background: catch up now, not on the next tick.
    onVisibilityChange() {
      if (document.visibilityState === 'visible') this.now = Date.now();
    },
  },
};
</script>

<style scoped lang="scss">
$aqua: #7fc0c3;
$zest: #D8F789;

// Sits in the card's button row and lays out by the card's container (free-pt-promo in
// FreePtPromo.vue). Wide card: no box, the label over the digits, beside the button.
.free-pt-countdown {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 3px;
  min-height: 48px;
}

.label {
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  white-space: nowrap;
  color: $aqua;
}

.digits {
  font-size: 19px;
  font-weight: 800;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: $zest;
}

.date {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  color: $zest;
}

// Narrower card: a box sized to its content, as tall as the button beside it (48px, then 50px
// under 700px), label and digits on one line. FreePtPromo wraps it under the button when the row is full.
@container free-pt-promo (max-width: 819px) {
  .free-pt-countdown {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 2px 12px;
    min-height: 48px;
    box-sizing: border-box;
    padding: 8px 14px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 12px;
    background: rgba(9, 22, 31, 0.55);
    text-align: center;
  }

  .label { font-size: 11px; }
  .digits { font-size: 18px; }
  .date { font-size: 15px; }
}

@container free-pt-promo (max-width: 699px) {
  .free-pt-countdown { min-height: 50px; }
}

// Phones: a full-width box under the full-width button (two centred lines when there isn't room).
@container free-pt-promo (max-width: 479.98px) {
  .free-pt-countdown {
    align-self: stretch;
    min-height: 46px;
  }
}
</style>
